import { useState, useCallback } from "react";
import { enqueueSnackbar } from "notistack";
import { useApiClient } from "../../../api";

export const useCheckForExistingAnswers = ({
  quizGroup,
  setQuizQuestions,
  setStudentsAnswers,
  setExistingAnswers = () => {},
  setShowMarks = () => {},
  setDisableChangeResponses = () => {},
  setSelectedQuizId = () => {},
  teacherView = false,
}) => {
  const ApiClient = useApiClient();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const checkForExistingAnswers = useCallback(
    async (selectedIdentifier) => {
      if (!quizGroup?.id) return;

      setLoading(true);
      setError(null);
      setStudentsAnswers({});
      setExistingAnswers({});
      setSelectedQuizId(null);

      try {
        const response = await ApiClient.quiz.checkForExistingResponses({
          identifier: selectedIdentifier?.id,
          quizGroup: quizGroup.id,
          teacherView,
        });
        if (!response?.existingQuizResponses?.quiz?.id) {
          throw new Error("No eligible quiz was found for this student");
        }

        const { responses, quiz, showMarks, disableChangeResponses } =
          response.existingQuizResponses;
        setShowMarks(showMarks);
        setDisableChangeResponses(disableChangeResponses);
        setSelectedQuizId(quiz.id);

        // 1️⃣ Find quiz in group
        const quizFromGroup = quizGroup.quizzes.find((q) => q.id === quiz.id);

        if (!quizFromGroup) return;

        setQuizQuestions(quizFromGroup.quizQuestions);

        const parsedResponses =
          typeof responses === "string" ? JSON.parse(responses) : responses;

        const normalizedAnswers = Object.fromEntries(
          quizFromGroup.quizQuestions.map((qq) => [
            qq.id,
            parsedResponses?.[qq.id] || [],
          ]),
        );

        setStudentsAnswers(normalizedAnswers);
        setExistingAnswers(normalizedAnswers);
      } catch (err) {
        console.error("Error fetching existing answers", err);
        setError(err);
        enqueueSnackbar("There was a problem loading previous answers", {
          variant: "error",
        });
      } finally {
        setLoading(false);
      }
    },
    [
      ApiClient,
      quizGroup,
      setQuizQuestions,
      setStudentsAnswers,
      setExistingAnswers,
      setSelectedQuizId,
      teacherView,
    ],
  );

  return {
    checkForExistingAnswers,
    loading,
    error,
  };
};
